// ABC SEO/GEO — shared LIVE data layer.
// One place that talks to the deployed Worker API, caches results for the
// session, and shares the currently-selected site across every screen.
// Exposes window.SGLive: { useLive, transforms, prettySite, … }.
(function () {
  const API = 'https://aibizconnect-seo-geo.abres-account.workers.dev';
  // Bump to force a new bundle filename. Useful when a browser/edge has cached a
  // bad response for the previous asset URL (a stale SPA fallback served
  // index.html as .js, which the browser then refuses → blank app).
  const BUILD = '2026-08-01.3';
  try { console.info('ABC SEO/GEO build', BUILD); } catch (e) { /* noop */ }

  // ── API key resolution ────────────────────────────────────────
  // No admin key is hard-coded in the bundle. When the tool is embedded in
  // GoHighLevel, we ask the GHL parent for the signed user payload, exchange
  // it at /ghl/session for a short-lived SCOPED token, and use that. Outside
  // GHL (local/admin dev) we fall back to a key supplied via a build-time env
  // var (VITE_DEMO_KEY in .env.local) — which is absent from production builds.
  const DEMO_KEY = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_DEMO_KEY) || '';

  let currentKey = '';
  let _bootstrapped = false;
  let _resolveBoot;
  const bootPromise = new Promise((res) => { _resolveBoot = res; });
  function getKey() { return _bootstrapped ? Promise.resolve(currentKey) : bootPromise.then(() => currentKey); }
  function done(k) { currentKey = k || ''; _bootstrapped = true; _resolveBoot(); }

  // ── Per-client session storage ────────────────────────────────
  // Each client/sub-account keeps its OWN session under abc_sess_<clientId>, so
  // switching between GHL sub-accounts reuses each one's session instead of one
  // shared slot that gets overwritten (which forced a re-login every switch).
  function saveSess(clientId, token) {
    try {
      if (clientId) localStorage.setItem('abc_sess_' + clientId, token);
      localStorage.setItem('abc_session', token);
      localStorage.setItem('abc_session_client', clientId || '');
    } catch (e) { /* ignore */ }
  }
  function readSess(clientId) {
    try {
      if (clientId) {
        const t = localStorage.getItem('abc_sess_' + clientId);
        if (t) return t;
        // migrate a legacy single-slot session that belongs to this client
        const legacy = localStorage.getItem('abc_session');
        if (legacy && localStorage.getItem('abc_session_client') === clientId) { localStorage.setItem('abc_sess_' + clientId, legacy); return legacy; }
        return '';
      }
      return localStorage.getItem('abc_session') || '';
    } catch (e) { return ''; }
  }
  function clearSess(clientId) {
    try {
      if (clientId) localStorage.removeItem('abc_sess_' + clientId);
      localStorage.removeItem('abc_session');
      localStorage.removeItem('abc_session_client');
    } catch (e) { /* ignore */ }
  }

  // ── GHL SSO (seamless auto-login inside the GHL iframe) ───────
  // Ask the GHL parent for the signed user payload, exchange it at /ghl/session
  // for a scoped token → the embedded app logs itself in per sub-account with NO
  // password (how other GHL apps "stick" when you switch sub-accounts). Resolves
  // to {token, clientId} or null (not embedded / SSO key not set / timeout).
  function trySSO() {
    return new Promise((resolve) => {
      try { if (typeof window === 'undefined' || window.parent === window) { resolve(null); return; } } catch (e) { resolve(null); return; }
      let settled = false;
      const fin = (v) => { if (!settled) { settled = true; try { window.removeEventListener('message', onMsg); } catch (e) { /* ignore */ } resolve(v); } };
      const onMsg = (e) => {
        const d = (e && e.data) || {};
        if (d.message === 'REQUEST_USER_DATA_RESPONSE' && d.payload) {
          fetch(API + '/ghl/session', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ encryptedData: d.payload }),
          }).then((r) => r.json())
            .then((j) => fin(j && j.success && j.data && j.data.token ? { token: j.data.token, clientId: j.data.clientId || '' } : null))
            .catch(() => fin(null));
        }
      };
      try { window.addEventListener('message', onMsg); window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*'); } catch (e) { fin(null); return; }
      setTimeout(() => fin(null), 2000);
    });
  }

  (function bootstrapKey() {
    try {
      const params = new URLSearchParams(window.location.search);
      const loginParam = params.get('login');
      const keyParam = params.get('key');
      const magicParam = params.get('magic');

      // Magic-link landing (standalone / passwordless). Exchange the one-time
      // token for a session, persist it, strip the token from the URL, then boot.
      if (magicParam) {
        fetch(API + '/auth/magic', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: magicParam }),
        }).then((r) => r.json()).then((j) => {
          const ok = j && j.success && j.data && j.data.token;
          if (ok) { saveSess(j.data.clientId || '', j.data.token); }
          try { const u = new URL(window.location.href); u.searchParams.delete('magic'); window.history.replaceState({}, '', u.toString()); } catch (e) { /* ignore */ }
          done(ok ? j.data.token : '');
        }).catch(() => {
          try { const u = new URL(window.location.href); u.searchParams.delete('magic'); window.history.replaceState({}, '', u.toString()); } catch (e) { /* ignore */ }
          done('');
        });
        return;
      }

      // Admin/legacy ?key= wins when explicitly present in the URL.
      if (keyParam) { sessionStorage.setItem('abc_key', keyParam); done(keyParam); return; }

      // GHL sub-account auto-login. BOTH menu-link shapes work:
      //   ?login=ghl_{{location.id}}   (legacy; here ?loc= is the business NAME)
      //   ?loc={{location.id}}         (standalone merge field — GHL renders this
      //                                 most reliably; same shape as Occasions)
      // A location id is alphanumeric, so anything else (an un-rendered
      // "{{location.id}}", or a business name with spaces) is never treated as one.
      const isLocId = (s) => /^[A-Za-z0-9]{6,40}$/.test(s);
      const fromLogin = (loginParam && /^ghl_/.test(loginParam)) ? loginParam.slice(4).trim() : '';
      const locParam = (params.get('loc') || '').trim();
      const locId = isLocId(fromLogin) ? fromLogin : (isLocId(locParam) ? locParam : '');
      if (locId) {
        const key = 'ghl_' + locId;
        const tok = readSess(key);
        if (tok) { done(tok); return; }
        // Business name: explicit ?name=, else ?loc= when it isn't the id itself.
        const nm = (params.get('name') || (locParam && locParam !== locId ? locParam : '')).trim();
        fetch(API + '/ghl/loc-session', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ login: key, name: nm }),
        }).then((r) => r.json()).then((j) => {
          if (j && j.success && j.data && j.data.token) { saveSess(j.data.clientId || key, j.data.token); done(j.data.token); }
          else { done(''); }   // fall back to the password gate
        }).catch(() => done(''));
        return;
      }

      // ?login= present but not a usable GHL id (non-GHL client, or GHL never
      // rendered the merge field) → per-client session if we have one, else gate.
      if (loginParam) {
        const tok = readSess(loginParam);
        if (tok) { done(tok); return; }
        done(''); return;
      }
      // No ?login= (standalone / legacy) → the generic session if present.
      const sess = readSess('');
      if (sess) { done(sess); return; }

      // Legacy saved key from an earlier ?key= visit.
      const savedKey = sessionStorage.getItem('abc_key');
      if (savedKey) { done(savedKey); return; }
    } catch { /* ignore */ }

    // Standalone / dev (no ?login=): try GHL SSO if embedded, else dev/admin fallback.
    trySSO().then((res) => done(res && res.token ? res.token : DEMO_KEY));
  })();

  // ── Per-client password login ─────────────────────────────────
  function login(clientId, password) {
    return fetch(API + '/auth/login', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login: clientId, password }),
    }).then((r) => r.json()).then((j) => {
      if (j && j.success && j.data && j.data.token) {
        currentKey = j.data.token;
        saveSess(clientId, j.data.token);
        return { ok: true };
      }
      return { ok: false, message: (j && j.error) || 'Incorrect password' };
    }).catch(() => ({ ok: false, message: 'Network error — please try again.' }));
  }
  // ── Standalone / passwordless entry ───────────────────────────
  // One call handles both "email me a sign-in link" and "start a free trial":
  // the server creates a trial if the email is new, else emails a login link.
  // Response is intentionally generic (no account enumeration).
  function startAuth(email, extra) {
    return fetch(API + '/auth/start', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({ email }, extra || {})),
    }).then((r) => r.json().then((j) => ({ status: r.status, j }))).then(({ j }) => {
      if (j && j.success) return { ok: true };
      return { ok: false, message: (j && j.error) || 'Something went wrong. Please try again.' };
    }).catch(() => ({ ok: false, message: 'Network error — please try again.' }));
  }
  function logout() {
    const tok = currentKey || '';
    let cid = '';
    try { cid = new URLSearchParams(window.location.search).get('login') || localStorage.getItem('abc_session_client') || ''; } catch (e) { /* ignore */ }
    const finish = () => {
      clearSess(cid);   // only THIS sub-account's session; other sub-accounts stay signed in
      try { sessionStorage.removeItem('abc_key'); } catch (e) { /* ignore */ }
      currentKey = '';
      try { const u = new URL(window.location.href); u.searchParams.delete('key'); window.location.replace(u.toString()); } catch (e) { window.location.reload(); }
    };
    if (tok.indexOf('sess_') === 0) {
      // Best-effort server-side revoke so the token can't be replayed.
      fetch(API + '/auth/logout', { method: 'POST', headers: { 'X-API-Key': tok } }).then(finish, finish);
    } else finish();
  }
  function fetchWhoami(clientId) {
    return fetch(API + '/auth/whoami?login=' + encodeURIComponent(clientId))
      .then((r) => r.json()).then((j) => (j && j.success ? j.data : null)).catch(() => null);
  }
  // Gate hook: 'checking' | 'authed' | 'needsLogin'. Validates the resolved key
  // against /api/account so an expired session falls back to the login screen.
  function useAuthGate() {
    const [state, setState] = React.useState('checking');
    React.useEffect(() => {
      let alive = true;
      (async () => {
        const key = await getKey();
        if (!key) { if (alive) setState('needsLogin'); return; }
        try {
          const r = await apiFetch('/api/account');
          if (!alive) return;
          if (r.ok) {
            // A freshly-enrolled sub-account has a temp password and must pick
            // its own before reaching the app.
            let mustChange = false;
            try { const j = await r.json(); mustChange = !!(j && j.data && j.data.mustChangePassword); } catch (e) { /* ignore */ }
            setState(mustChange ? 'mustChange' : 'authed');
            return;
          }
          // Expired/invalid session for this client — clear just its slot.
          try { clearSess(new URLSearchParams(window.location.search).get('login') || ''); } catch (e) { /* ignore */ }
          currentKey = '';
          setState('needsLogin');
        } catch { if (alive) setState('needsLogin'); }
      })();
      return () => { alive = false; };
    }, []);
    return { state, setAuthed: () => setState('authed') };
  }

  // Self-serve password change (forced first-login after admin enrollment).
  function changeOwnPassword(password) {
    return apiFetch('/api/clients/me/change-password', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }),
    }).then((r) => r.json()).catch((e) => ({ success: false, error: String(e) }));
  }

  // fetch helper that injects the resolved key
  async function apiFetch(path, opts) {
    const key = await getKey();
    const o = opts || {};
    const headers = Object.assign({}, o.headers, key ? { 'X-API-Key': key } : {});
    return fetch(API + path, Object.assign({}, o, { headers }));
  }

  // ── session caches (survive screen switches, one fetch per resource) ──
  let sitesPromise = null;
  const perfPromises = {};

  // ── Google reconnect needed (refresh token revoked/expired) ──
  let reauthNeeded = false;
  const reauthSubs = new Set();
  function markReauth() { if (!reauthNeeded) { reauthNeeded = true; reauthSubs.forEach((f) => f(true)); } }
  function clearReauth() { reauthNeeded = false; reauthSubs.forEach((f) => f(false)); }
  // Inspect a parsed API response; flag a Google reconnect if its grant died.
  function checkReauth(j) { if (j && j.success === false && j.error === 'reauth_required') markReauth(); return j; }

  // ── shared "current site", with a tiny pub/sub so every mounted screen
  //    re-renders when the user changes the site on any one of them ──
  let currentSite = '';
  const subs = new Set();
  function setCurrentSite(s) { currentSite = s; subs.forEach((f) => f(s)); }
  // The currently-selected domain (bare) — appended to keyword/rank calls so
  // they're scoped per-domain. '' when nothing is selected yet.
  function curDomain() { return currentSite ? prettySite(currentSite) : ''; }
  function domQ(sep) { const d = curDomain(); return d ? (sep || '?') + 'domain=' + encodeURIComponent(d) : ''; }

  // Notify every screen (esp. the top-bar selector) when the tracked-domain set
  // changes, so adding/removing a domain in Settings updates the picker live.
  const domainSubs = new Set();
  function notifyDomainsChanged() { domainSubs.forEach((f) => f()); }

  const prettySite = (s) => (s || '')
    .replace('sc-domain:', '').replace(/^https?:\/\//, '').replace(/\/$/, '');

  function fetchSites() {
    if (!sitesPromise) {
      sitesPromise = apiFetch('/api/connections/gsc/sites')
        .then((r) => r.json())
        .then((j) => { checkReauth(j); return (j.data || []).map((s) => s.siteUrl); });
    }
    return sitesPromise;
  }

  // Google Search Console sometimes logs advanced search-operator strings as
  // "queries" against a site — e.g. "-site:facebook.com -site:youtube.com …",
  // or inurl:/intitle:/filetype: scraper noise. These are NOT real keywords, so
  // we strip them before anything (ranking table, scoring, opportunities) sees them.
  function isJunkQuery(q) {
    if (!q) return true;
    const s = String(q).toLowerCase();
    // any Google search operator (with or without a leading "-") → not a keyword
    if (/(^|[\s"'(])-?(site|inurl|intitle|intext|allintitle|allinurl|allintext|filetype|ext|cache|related|before|after|around|loc|map|source|stocks|weather|movie):/.test(s)) return true;
    return false;
  }
  function sanitizePerf(data) {
    if (data && Array.isArray(data.byQuery)) {
      return { ...data, byQuery: data.byQuery.filter((r) => r && r.keys && !isJunkQuery(r.keys[0])) };
    }
    return data;
  }

  function fetchPerf(site) {
    if (!perfPromises[site]) {
      perfPromises[site] = apiFetch('/api/connections/gsc/performance?site=' + encodeURIComponent(site))
        .then((r) => r.json())
        .then((j) => { checkReauth(j); return (j.success ? sanitizePerf(j.data) : Promise.reject(j.error || 'request failed')); });
    }
    return perfPromises[site];
  }

  // Prefer a `sc-domain:` property — they aggregate every protocol/subdomain
  // and hold the real traffic (URL-prefix properties are often empty).
  const pickDefault = (list) =>
    list.find((s) => s.indexOf('sc-domain:') === 0) ||
    list[0] || '';

  // ── account / domain allowance (meter + gate) ──────────────────
  let accountPromise = null;
  function fetchAccount(force) {
    if (force) accountPromise = null;
    if (!accountPromise) {
      accountPromise = apiFetch('/api/account').then((r) => r.json())
        .then((j) => j.data || { plan: 'starter', domainLimit: 1, used: 0, domains: [] });
    }
    return accountPromise;
  }
  async function addDomain(property, label) {
    const r = await apiFetch('/api/account/domains', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ property, label }),
    });
    accountPromise = null;
    const j = await r.json();
    if (j && j.success !== false) notifyDomainsChanged();
    return j;
  }
  async function removeDomain(property) {
    await apiFetch('/api/account/domains?property=' + encodeURIComponent(property), { method: 'DELETE' });
    accountPromise = null;
    notifyDomainsChanged();
  }
  // Guarantee at least one tracked domain (auto-track the default GSC property
  // on first use) so the dashboards have data. Returns the tracked properties.
  async function ensureTrackedDomains() {
    let acc = await fetchAccount();
    let tracked = (acc.domains || []).map((d) => d.property);
    if (tracked.length === 0 && acc.domainLimit > 0) {
      const all = await fetchSites();
      const def = pickDefault(all);
      if (def) {
        await addDomain(def, prettySite(def));
        acc = await fetchAccount(true);
        tracked = (acc.domains || []).map((d) => d.property);
      }
    }
    return tracked;
  }

  // React hook: returns live performance for the shared current site.
  function useLive() {
    const [sites, setSites] = React.useState(null);
    const [site, setSite] = React.useState(currentSite);
    const [perf, setPerf] = React.useState(null);
    const [err, setErr] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    // subscribe to shared site changes
    React.useEffect(() => {
      const f = (v) => setSite(v);
      subs.add(f);
      return () => subs.delete(f);
    }, []);

    // load the TRACKED domains (auto-tracks the default on first use); seed the
    // shared current site if unset or no longer in the tracked set. Re-runs when
    // domains change in Settings so the top-bar picker updates without a refresh.
    React.useEffect(() => {
      let live = true;
      const loadTracked = () => {
        ensureTrackedDomains()
          .then((list) => {
            if (!live) return;
            setSites(list);
            if ((!currentSite || list.indexOf(currentSite) < 0) && list.length) setCurrentSite(pickDefault(list));
            if (!list.length) setLoading(false);
          })
          .catch((e) => { if (live) { setErr(String(e)); setLoading(false); } });
      };
      loadTracked();
      domainSubs.add(loadTracked);
      return () => { live = false; domainSubs.delete(loadTracked); };
    }, []);

    // (re)load performance whenever the active site changes
    React.useEffect(() => {
      if (!site) return;
      let live = true;
      setLoading(true); setErr(null);
      fetchPerf(site)
        .then((d) => { if (live) { setPerf(d); setLoading(false); } })
        .catch((e) => { if (live) { setErr(String(e)); setLoading(false); } });
      return () => { live = false; };
    }, [site]);

    return { sites, site, setSite: setCurrentSite, perf, err, loading, prettySite };
  }

  // Reconnect ALL Google services in ONE consent → a single refresh token for
  // everything, so reconnecting one service can't invalidate the others' grant.
  function reconnectGoogle() { clearReauth(); return connectGoogle('gsc,ga,gbp,youtube'); }
  // React hook: is a Google reconnect needed? (refresh token revoked/expired)
  function useReauth() {
    const [need, setNeed] = React.useState(reauthNeeded);
    React.useEffect(() => { const f = (v) => setNeed(v); reauthSubs.add(f); setNeed(reauthNeeded); return () => reauthSubs.delete(f); }, []);
    return need;
  }

  // React hook for the account/allowance meter (used by the Domains manager).
  function useAccount() {
    const [acc, setAcc] = React.useState(null);
    const reload = React.useCallback(() => fetchAccount(true).then(setAcc), []);
    React.useEffect(() => { fetchAccount().then(setAcc); }, []);
    return [acc, reload];
  }

  // Entitlement to paid-data tools (DataForSEO). paidData: null=loading, true/false known.
  // ── Usage refresh signal ─────────────────────────────────────
  // Approving or removing keywords changes the account-wide tracked-rankings
  // total. Without a signal the sidebar meter kept whatever it read on mount
  // and looked wrong until a full reload.
  let _usageVersion = 0;
  const _usageSubs = new Set();
  function bumpUsage() { _usageVersion += 1; _usageSubs.forEach((fn) => { try { fn(_usageVersion); } catch (e) { /* noop */ } }); }
  function useUsageVersion() {
    const [v, setV] = React.useState(_usageVersion);
    React.useEffect(() => { _usageSubs.add(setV); return () => { _usageSubs.delete(setV); }; }, []);
    return v;
  }

  function useEntitlement() {
    const [acc, setAcc] = React.useState(null);
    React.useEffect(() => { fetchAccount().then(setAcc); }, []);
    return {
      loading: !acc,
      paidData: acc ? !!acc.paidData : null,
      status: acc ? acc.status : null,
      trialEndsAt: acc ? (acc.trialEndsAt ?? null) : null,
      plan: acc ? acc.plan : null,
      admin: acc ? !!acc.admin : false,
    };
  }

  // White-label branding: a client's own brand overrides the AI Biz Connect default.
  const ABC_BRAND = { name: 'AI Biz Connect', short: 'ABC SEO/GEO', logo: null, accent: '#2563EB', supportEmail: 'info@AIBizConnect.ca', supportPhone: '+1 (416) 727-7111', website: 'www.AIBizConnect.ca', whitelabel: false };
  function useBranding() {
    const [b, setB] = React.useState(ABC_BRAND);
    React.useEffect(() => {
      fetchAccount().then((acc) => {
        if (acc && acc.whitelabel && acc.branding) {
          const br = acc.branding;
          setB({
            name: br.name || 'Your Brand',
            short: br.name || 'Your Brand',
            logo: br.logo || null,
            accent: br.accent || ABC_BRAND.accent,
            supportEmail: br.supportEmail || '',
            supportPhone: br.supportPhone || '',
            website: '',
            whitelabel: true,
          });
        }
      });
    }, []);
    return b;
  }

  // Locked-upsell card shown on paid-data screens during a trial.
  function PaidLock({ title, blurb }) {
    const K = window.SGKit, I = window.SGIcons;
    if (!K || !I) return null;
    return (
      React.createElement(K.Card, { style: { padding: 28, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 } },
        React.createElement('span', { style: { width: 56, height: 56, borderRadius: 16, background: 'var(--sg-violet-100)', color: 'var(--sg-violet-700)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' } },
          React.createElement(I.Icon, { name: 'lock', size: 26 })),
        React.createElement('div', { style: { fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 20, color: 'var(--sg-text)' } }, title || 'Unlock on a paid plan'),
        React.createElement('p', { style: { maxWidth: 460, fontSize: 13.5, color: 'var(--sg-text-2)', lineHeight: 1.6, margin: 0 } },
          blurb || 'This tool uses premium search data and is available on a paid plan. Your free trial includes AI-visibility scoring, the site audit, Core Web Vitals, Search Console insights and Local Listings.'),
        React.createElement('button', { onClick: function () { try { window.parent && window.parent.postMessage({ type: 'abc-upgrade' }, '*'); } catch (e) { /* noop */ } },
          style: { marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 8, height: 42, padding: '0 22px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 14, color: '#fff', background: 'var(--sg-grad-violet, var(--sg-grad-brand))', boxShadow: '0 3px 10px rgba(124,58,237,0.28)' } },
          React.createElement(I.Icon, { name: 'sparkles', size: 16 }), 'Upgrade to unlock')
      )
    );
  }

  // ── GEO / AI-Visibility (Workers-AI scored) ────────────────────────
  let mePromise = null;
  function fetchClientMe(force) {
    if (force) mePromise = null;
    if (!mePromise) {
      mePromise = apiFetch('/api/clients/me').then((r) => r.json())
        .then((j) => (j.success ? j.data : null)).catch(() => null);
    }
    return mePromise;
  }
  function fetchGeoSummary(domain) {
    const q = domain ? '?domain=' + encodeURIComponent(prettySite(domain)) : '';
    return apiFetch('/api/geo/summary' + q).then((r) => r.json()).then((j) => j.data || null).catch(() => null);
  }
  async function runGeoScan(domain) {
    const r = await apiFetch('/api/geo/run-sync', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(domain ? { domain } : {}),
    });
    mePromise = null; // invalidate cached score
    const j = await r.json();
    if (!j.success) throw new Error(j.error || 'scan failed');
    return j.data;
  }
  async function runSeoAudit(domain) {
    const r = await apiFetch('/api/audit/run-sync', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(domain ? { url: 'https://' + domain } : {}),
    });
    mePromise = null;
    const j = await r.json();
    if (!j.success) throw new Error(j.error || 'audit failed');
    return j.data;
  }
  function fetchGeoMentions(domain) {
    const q = domain ? '?domain=' + encodeURIComponent(prettySite(domain)) : '';
    return apiFetch('/api/geo/mentions' + q).then((r) => r.json()).then((j) => j.data || []).catch(() => []);
  }
  // ── DataForSEO-backed (keyword research, backlinks, competitors) ──
  function researchKeywords(keywords, locationCode) {
    const body = { keywords };
    if (locationCode) body.location_code = locationCode;
    return apiFetch('/api/keywords/research' + domQ(), {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => r.json()).then((j) => { if (j && j.success) bumpUsage(); return j.success
      ? { ok: true, data: j.data || [], cappedTracking: !!j.ranking_limit_reached, rankingLimit: j.ranking_limit }
      : { ok: false, error: j.error, message: j.message || 'Could not complete the request.' };
    }).catch(() => ({ ok: false, error: 'network', message: 'Network error — please try again.' }));
  }
  // ── Approved keyword set (client-curated working list; approval is free) ──
  function fetchApprovedKeywords() {
    return apiFetch('/api/keywords/approved' + domQ())
      .then((r) => r.json()).then((j) => (j.success ? j.data : [])).catch(() => []);
  }
  // FREE candidate keywords extracted from the client's own homepage
  // (title, headings, meta keywords, top body phrases).
  function fetchWebsiteKeywords() {
    return apiFetch('/api/keywords/from-content' + domQ())
      .then((r) => r.json()).then((j) => (j.success && Array.isArray(j.data) ? j.data : [])).catch(() => []);
  }
  // The client's CHOSEN keywords with their latest real SERP position
  // (rankings table) — [{ keyword, volume, difficulty, position, previous_position, url, checked_at }].
  function fetchTrackedKeywords() {
    return apiFetch('/api/keywords/tracked' + domQ())
      .then((r) => r.json()).then((j) => (j.success && Array.isArray(j.data) ? j.data : [])).catch(() => []);
  }
  // Head-to-head on the client's CHOSEN keywords: your position vs each saved
  // competitor's → { competitors: [...], keywords: [{ keyword, you, competitors, behind }] }
  function fetchKeywordGap() {
    return apiFetch('/api/competitors/keyword-gap' + domQ())
      .then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  // The client's own market + industry — rankings, keyword volumes and AI prompts
  // are all measured against these.
  // Market is PER DOMAIN — domQ() scopes it to the site you're viewing.
  function fetchProfile() {
    return apiFetch('/api/clients/me/profile' + domQ()).then((r) => r.json())
      .then((j) => (j.success && j.data ? j.data : null))
      .catch(() => null);
  }
  // What we READ off the website — brand, trade, city, services and the phrases
  // its own pages target. Stored per domain, so this is a cheap lookup, not a
  // crawl.
  // The plan table, straight from the server's tier config. The signup screen
  // used to hold its own copy of the prices and allowances, which could drift
  // from what the platform actually enforces.
  function fetchPlans() {
    return fetch(API + '/auth/plans').then((r) => r.json())
      .then((j) => (j && j.success ? j.data : null)).catch(() => null);
  }
  function fetchDetectedProfile() {
    return apiFetch('/api/clients/me/profile/detected' + domQ()).then((r) => r.json())
      .then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  // Re-read the website. `force` overwrites values the user typed in.
  function detectProfile(force) {
    const q = domQ();
    const url = '/api/clients/me/profile/detect' + (q ? q + (force ? '&force=1' : '') : (force ? '?force=1' : ''));
    return apiFetch(url, { method: 'POST' }).then((r) => r.json())
      .then((j) => (j && j.success ? { ok: true, ...j.data } : { ok: false, error: (j && j.error) || 'Could not read that website.' }))
      .catch(() => ({ ok: false, error: 'Network error — please try again.' }));
  }
  function saveProfile(patch) {
    return apiFetch('/api/clients/me/profile' + domQ(), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
    }).then((r) => r.json()).then((j) => (j && j.success ? { ok: true, data: j.data } : { ok: false, error: (j && j.error) || 'Could not save.' }))
      .catch(() => ({ ok: false, error: 'Network error — please try again.' }));
  }
  // Automations ("auto-pilot") — the recurring jobs that already run on cron.
  function fetchAutomations() {
    return apiFetch('/api/clients/automations')
      .then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  function setAutomation(patch) {
    return apiFetch('/api/clients/automations', {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
    }).then((r) => r.json()).then((j) => !!(j && j.success)).catch(() => false);
  }
  // Internal-linking opportunities across the client's own pages.
  function fetchInternalLinks() {
    return apiFetch('/api/audit/internal-links' + domQ())
      .then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  // "Find similar" for a seed keyword: your own "<phrase> in <place>" patterns
  // applied to the other places you target, plus real related searches.
  function fetchSimilarKeywords(seed, n) {
    const qs = new URLSearchParams();
    qs.set('seed', seed);
    qs.set('n', String(n || 10));
    const d = curDomain(); if (d) qs.set('domain', d);
    return apiFetch('/api/keywords/similar?' + qs.toString())
      .then((r) => r.json())
      .then((j) => { if (j && j.success) bumpUsage(); return j.success ? { ok: true, data: j.data || [] } : { ok: false, error: j.error || 'Could not find similar keywords.' }; })
      .catch(() => ({ ok: false, error: 'Network error — please try again.' }));
  }
  // Always up to N real related keywords, from Search Console + competitor gap +
  // site content/meta (server-side, excludes approved). realQueries = GSC queries.
  function fetchSuggestedKeywords(n, realQueries) {
    const qs = new URLSearchParams();
    qs.set('n', String(n || 5));
    const d = curDomain(); if (d) qs.set('domain', d);
    if (realQueries && realQueries.length) qs.set('realq', realQueries.slice(0, 20).join(','));
    return apiFetch('/api/keywords/suggest?' + qs.toString())
      .then((r) => r.json())
      .then((j) => { if (j && j.success) bumpUsage(); return (j.success && Array.isArray(j.data)) ? j.data : []; })
      .catch(() => []);
  }
  // AI keyword ideas matched to the client's business, locale & clientele.
  // `exclude` = keywords already shown/approved so "show more" gives fresh ones.
  function fetchKeywordIdeas(count, exclude, domain, seeds, realQueries) {
    const qs = new URLSearchParams();
    if (count) qs.set('count', String(count));
    if (exclude && exclude.length) qs.set('exclude', exclude.slice(0, 60).join(','));
    if (domain) qs.set('domain', prettySite(domain));
    if (seeds && seeds.length) qs.set('seed', seeds.slice(0, 12).join(','));
    if (realQueries && realQueries.length) qs.set('realq', realQueries.slice(0, 20).join(','));
    const q = qs.toString();
    return apiFetch('/api/keywords/ideas' + (q ? '?' + q : ''))
      .then((r) => r.json())
      .then((j) => (j && j.success
        ? { ok: true, data: j.data || [], exhausted: !!j.exhausted, approved: j.approved, limit: j.limit, remaining: j.remaining, message: j.message }
        : { ok: false, error: (j && j.error) || 'failed' }))
      .catch(() => ({ ok: false, error: 'network' }));
  }
  // Check Google rankings for a set of keywords now (per-domain, country-resolved
  // server-side). Populates the rankings table used by Rank Tracking + Keywords.
  // Pass a keyword list, or nothing to check ALL of this domain's tracked keywords.
  function checkRanks(keywords) {
    const kws = (keywords || []).map((k) => String(k).trim()).filter(Boolean).slice(0, 100);
    const body = kws.length ? { keywords: kws, device: 'desktop' } : { device: 'desktop' };
    return apiFetch('/api/keywords/check-ranks' + domQ(), {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => r.json())
      .then((j) => (j.success ? { ok: true, data: j.data, checked: j.checked, found: j.found, queued: j.queued } : { ok: false, error: j.error }))
      .catch(() => ({ ok: false, error: 'Network error — please try again.' }));
  }
  function approveKeywords(list) {
    return apiFetch('/api/keywords/approve' + domQ(), {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ keywords: list }),
    }).then((r) => r.json()).then((j) => {
      const ok = !!(j && j.success);
      if (ok) bumpUsage();   // the shared tracked-rankings total just changed
      // Immediately rank-check the just-added keywords so positions populate now,
      // instead of waiting for the daily cron. Fire-and-forget — never blocks approve.
      if (ok && j.added > 0 && Array.isArray(list) && list.length) { try { checkRanks(list); } catch (e) { /* noop */ } }
      return ok
        ? { ok: true, approved: j.approved, added: j.added, capped: !!j.capped, limit: j.limit, requested: j.requested, skipped: j.skipped, used: j.used }
        : { ok: false, error: j.error, message: j.message || 'Could not approve keywords.' };
    }).catch(() => ({ ok: false, error: 'network', message: 'Network error — please try again.' }));
  }
  function removeApprovedKeyword(kw) {
    return apiFetch('/api/keywords/approved?keyword=' + encodeURIComponent(kw) + domQ('&'), { method: 'DELETE' })
      .then((r) => r.json()).then((j) => { if (j && j.success) bumpUsage(); return { ok: !!j.success }; })
      .catch(() => ({ ok: false }));
  }
  // Content Studio: generate a SEO/GEO article + social campaign from a keyword.
  function generateContent(keyword, opts) {
    const body = Object.assign({ keyword }, opts || {});
    return apiFetch('/api/content/generate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => r.json()).then((j) => (j.success
      ? { ok: true, ...j.data }
      : { ok: false, error: j.error, message: j.message || j.error || 'Content generation failed.' }
    )).catch(() => ({ ok: false, error: 'network', message: 'Network error — please try again.' }));
  }
  // Related/"similar" keywords for a seed (DataForSEO keywords-for-keywords).
  function fetchKeywordSuggestions(seed, locationCode, limit) {
    const body = { seed, limit: limit || 30 };
    if (locationCode) body.location_code = locationCode;
    return apiFetch('/api/keywords/suggestions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => r.json()).then((j) => (j.success
      ? { ok: true, data: j.data || [] }
      : { ok: false, error: j.error, message: j.message || 'Could not load suggestions.' }
    )).catch(() => ({ ok: false, error: 'network', message: 'Network error — please try again.' }));
  }
  // `cachedOnly` asks the server for what it ALREADY has, without calling (or
  // paying) DataForSEO. That's how a screen restores itself on revisit instead
  // of making the user press "Load" every time.
  function blQuery(domain, cachedOnly, extra) {
    const qs = [];
    if (domain) qs.push('domain=' + encodeURIComponent(domain));
    if (cachedOnly) qs.push('cached=1');
    if (extra) qs.push(extra);
    return qs.length ? '?' + qs.join('&') : '';
  }
  function fetchBacklinks(domain, cachedOnly) {
    return apiFetch('/api/backlinks/overview' + blQuery(domain, cachedOnly))
      .then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  function fetchCompetitors(domain) {
    return apiFetch('/api/competitors/organic' + (domain ? '?domain=' + encodeURIComponent(domain) : ''))
      .then((r) => r.json()).then((j) => (j.success ? j.data : [])).catch(() => []);
  }
  // User-named competitors — scoped per owner domain (the selected site).
  function fetchSavedCompetitors(owner) {
    return apiFetch('/api/competitors/saved' + (owner ? '?owner=' + encodeURIComponent(owner) : '')).then((r) => r.json())
      .then((j) => (j.success ? { ok: true, domains: j.data || [], owner: j.owner } : { ok: false, domains: [] }))
      .catch(() => ({ ok: false, domains: [] }));
  }
  function addCompetitor(owner, domain) {
    return apiFetch('/api/competitors/saved', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ owner, domain }),
    }).then((r) => r.json()).then((j) => (j.success ? { ok: true, domains: j.data || [] } : { ok: false, error: j.error }))
      .catch(() => ({ ok: false, error: 'Network error — please try again.' }));
  }
  function removeCompetitor(owner, domain) {
    const qs = '?domain=' + encodeURIComponent(domain) + (owner ? '&owner=' + encodeURIComponent(owner) : '');
    return apiFetch('/api/competitors/saved' + qs, { method: 'DELETE' })
      .then((r) => r.json()).then((j) => (j.success ? { ok: true, domains: j.data || [] } : { ok: false }))
      .catch(() => ({ ok: false }));
  }
  // Real head-to-head metrics for your domain + each saved competitor.
  function compareCompetitors(domain) {
    return apiFetch('/api/competitors/compare' + (domain ? '?domain=' + encodeURIComponent(domain) : ''))
      .then((r) => r.json()).then((j) => (j.success ? { ok: true, rows: j.data || [] } : { ok: false, error: j.error, rows: [] }))
      .catch(() => ({ ok: false, rows: [] }));
  }
  // ── Admin (master key only) ────────────────────────────────────
  function adminListClients() {
    return apiFetch('/api/clients').then((r) => r.json()).then((j) => (j.success ? j.data : [])).catch(() => []);
  }
  function adminUpdateClient(id, patch) {
    return apiFetch('/api/clients/' + encodeURIComponent(id), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
    }).then((r) => r.json()).catch((e) => ({ success: false, error: String(e) }));
  }
  function adminIssueLink(id) {
    return apiFetch('/api/clients/' + encodeURIComponent(id) + '/access-link', { method: 'POST' })
      .then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  function adminSetPassword(id, password) {
    return apiFetch('/api/clients/' + encodeURIComponent(id) + '/password', {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }),
    }).then((r) => r.json()).catch((e) => ({ success: false, error: String(e) }));
  }
  function adminAuditLog(limit) {
    return apiFetch('/api/clients/audit-log?limit=' + (limit || 100))
      .then((r) => r.json()).then((j) => (j.success ? j.data : [])).catch(() => []);
  }
  function adminCreateClient(body) {
    return apiFetch('/api/clients', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => r.json()).then((j) => (j.success ? { ok: true, data: j.data } : { ok: false, error: j.error }))
      .catch(() => ({ ok: false, error: 'Network error — please try again.' }));
  }
  // Invite someone by email — creates a 14-day trial and emails them a magic
  // sign-in link. Returns { sent, link, email, created }.
  function adminInvite(body) {
    return apiFetch('/api/clients/invite', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => r.json()).then((j) => (j.success ? { ok: true, data: j.data } : { ok: false, error: j.error }))
      .catch(() => ({ ok: false, error: 'Network error — please try again.' }));
  }
  function adminDeleteClient(id) {
    return apiFetch('/api/clients/' + encodeURIComponent(id), { method: 'DELETE' })
      .then((r) => r.json()).then((j) => (j.success ? { ok: true, data: j.data } : { ok: false, error: j.error }))
      .catch(() => ({ ok: false, error: 'Network error — please try again.' }));
  }
  // GHL sub-account enrollment (master key only) ───────────────────
  // Un-enrolled subs that opened the SEO/GEO tab, waiting to be added.
  function adminListPending() {
    return apiFetch('/api/clients/pending').then((r) => r.json()).then((j) => (j.success ? j.data : [])).catch(() => []);
  }
  // Enroll a sub as ghl_<locationId> with a temp password (must-change on 1st login).
  function adminEnroll(body) {
    return apiFetch('/api/clients/enroll', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => r.json()).then((j) => (j.success ? { ok: true, data: j.data } : { ok: false, error: j.error }))
      .catch(() => ({ ok: false, error: 'Network error — please try again.' }));
  }
  function adminDismissPending(locationId) {
    return apiFetch('/api/clients/pending/' + encodeURIComponent(locationId), { method: 'DELETE' })
      .then((r) => r.json()).then((j) => !!(j && j.success)).catch(() => false);
  }
  function fetchYouTubeVideos() {
    return apiFetch('/api/youtube/videos').then((r) => r.json())
      .then((j) => (j.success ? { ok: true, videos: j.data } : { ok: false, error: j.error }))
      .catch(() => ({ ok: false, error: 'network' }));
  }
  function optimizeYouTubeVideo(id) {
    return apiFetch('/api/youtube/video/' + encodeURIComponent(id) + '/optimize').then((r) => r.json())
      .then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  // The NEXT five keyphrase ideas for a video (paged off a cached pool, so the
  // user can keep asking for 5 more without repeats).
  function moreVideoTags(id, offset) {
    return apiFetch('/api/youtube/video/' + encodeURIComponent(id) + '/tags?offset=' + (offset || 0))
      .then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  // Write optimized title/description/tags back to YouTube.
  function saveYouTubeVideo(id, changes) {
    return apiFetch('/api/youtube/video/' + encodeURIComponent(id) + '/save', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changes),
    }).then((r) => r.json())
      .then((j) => (j.success ? { ok: true, data: j.data } : { ok: false, error: j.error, detail: j.detail }))
      .catch(() => ({ ok: false, error: 'network' }));
  }
  // Open the Google consent flow for a set of providers (e.g. 'youtube', 'gsc,ga').
  function connectGoogle(providers) {
    return apiFetch('/api/connections/google/start?providers=' + encodeURIComponent(providers || 'gsc'))
      .then((r) => r.json()).then((j) => {
        const url = j.data && j.data.authUrl;
        if (url) {
          // Same-tab (top-window) redirect, NOT a popup — popups get silently
          // blocked by browsers and inside the GoHighLevel iframe. After consent,
          // the callback's "connected" page links back to the app.
          try { (window.top || window).location.assign(url); }
          catch (e) { window.location.assign(url); }
        }
        return !!url;
      }).catch(() => false);
  }
  function generateReport(type) {
    return apiFetch('/api/reports/generate' + domQ(), {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: type || 'on-demand' }),
    }).then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  // ── Report delivery (email to client + recurring schedule) ────
  function sendReport(opts) {
    return apiFetch('/api/reports/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opts || {}) })
      .then((r) => r.json()).catch(() => ({ success: false, error: 'Network error — please try again.' }));
  }
  function scheduleReport(opts) {
    return apiFetch('/api/reports/schedule', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opts || {}) })
      .then((r) => r.json()).catch(() => ({ success: false, error: 'Network error — please try again.' }));
  }
  function fetchScheduledReports(domain) {
    return apiFetch('/api/reports/scheduled' + (domain ? '?domain=' + encodeURIComponent(domain) : ''))
      .then((r) => r.json()).then((j) => (j.success ? j.data : [])).catch(() => []);
  }
  function toggleScheduledReport(id, active) {
    return apiFetch('/api/reports/scheduled/' + encodeURIComponent(id), { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active }) })
      .then((r) => r.json()).catch(() => ({ success: false }));
  }
  function deleteScheduledReport(id) {
    return apiFetch('/api/reports/scheduled/' + encodeURIComponent(id), { method: 'DELETE' })
      .then((r) => r.json()).catch(() => ({ success: false }));
  }
  function fetchReportOverview() {
    return apiFetch('/api/reports/overview').then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  // Link-building opportunities: industry-relevant competitor link gap.
  // Real Google Business Profile detection (matched by domain/phone).
  function checkGoogleListing(name, location, domain, phone) {
    const qs = new URLSearchParams();
    if (name) qs.set('name', name);
    if (location) qs.set('location', location);
    if (domain) qs.set('domain', domain);
    if (phone) qs.set('phone', phone);
    return apiFetch('/api/listings/google?' + qs.toString())
      .then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }
  // ── Agent readiness ────────────────────────────────────────────────
  // Two numbers on purpose: `externalScore` is what Cloudflare's free public
  // scanner will tell the client if they run it themselves, `score` is what
  // actually applies to their business. Never show one without the other —
  // a client who sees only our kinder number will think we are wrong.
  function fetchAgentReadiness(domain, hasApi) {
    const qs = new URLSearchParams();
    if (domain) qs.set('url', domain);
    if (hasApi) qs.set('hasApi', '1');
    const q = qs.toString();
    return apiFetch('/api/audit/agent-readiness' + (q ? '?' + q : ''))
      .then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }

  // ── Review behaviour (reply rate + response time) ──────────────────
  // Two calls because the upstream provider charges on task creation and
  // answers minutes later: start once, then collect (free, repeatable).
  function startReviewPull(body) {
    return apiFetch('/api/listings/reviews/start', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
    }).then((r) => r.json()).catch((e) => ({ success: false, error: String(e) }));
  }
  function collectReviews(taskId) {
    return apiFetch('/api/listings/reviews?taskId=' + encodeURIComponent(taskId))
      .then((r) => r.json()).then((j) => (j.success ? j.data : null)).catch(() => null);
  }

  function fetchBacklinkOpportunities(domain, cachedOnly) {
    return apiFetch('/api/backlinks/opportunities' + blQuery(domain, cachedOnly))
      .then((r) => r.json()).then((j) => (j.success && j.data ? { ok: true, ...j.data } : { ok: false, industry: [], needsCompetitors: false }))
      .catch(() => ({ ok: false, industry: [], needsCompetitors: false }));
  }
  function fetchReferringDomains(domain, limit, cachedOnly) {
    return apiFetch('/api/backlinks/referring-domains' + blQuery(domain, cachedOnly, limit ? 'limit=' + limit : ''))
      .then((r) => r.json()).then((j) => (j.success ? j.data : [])).catch(() => []);
  }
  function fetchLatestAudit(domain, type) {
    const parts = [];
    if (type) parts.push('type=' + type);
    if (domain) parts.push('url=' + encodeURIComponent('https://' + domain));
    const qs = parts.length ? '?' + parts.join('&') : '';
    return apiFetch('/api/audit/latest' + qs).then((r) => r.json()).then((j) => {
      if (!j.success) return null;
      const row = j.data || {};
      const d = row.data || {};
      return {
        score: row.score, url: row.url, crawledAt: d.crawledAt,
        checks: d.checks || [], summary: d.summary || null, pagespeed: d.pagespeed || null,
        raw: d, // full payload — GEO audits carry by_platform / mentions / share_of_voice
      };
    }).catch(() => null);
  }
  // React hook: client SEO+GEO scores, summaries, and scan triggers.
  // `domain` scopes the GEO summary + scans to the active site so a multi-domain
  // account doesn't show one domain's AI results under another.
  function useGeo(domain) {
    const [me, setMe] = React.useState(null);
    const [summary, setSummary] = React.useState(null);
    const [scanning, setScanning] = React.useState(false);
    const [scanningSeo, setScanningSeo] = React.useState(false);
    const reload = React.useCallback(() => {
      fetchClientMe(true).then(setMe); fetchGeoSummary(domain).then(setSummary);
    }, [domain]);
    // Re-fetch the (domain-scoped) summary whenever the selected domain changes.
    React.useEffect(() => { setSummary(null); fetchClientMe().then(setMe); fetchGeoSummary(domain).then(setSummary); }, [domain]);
    const scan = React.useCallback(async () => {
      setScanning(true);
      try { await runGeoScan(domain); reload(); } finally { setScanning(false); }
    }, [reload, domain]);
    const scanSeo = React.useCallback(async () => {
      setScanningSeo(true);
      try { await runSeoAudit(domain); reload(); } finally { setScanningSeo(false); }
    }, [reload, domain]);
    return {
      me, summary, scanning, scanningSeo, scan, scanSeo,
      scanned: !!(me && me.last_geo_audit_at),
      seoScanned: !!(me && me.last_seo_audit_at),
    };
  }

  // ── transforms: GSC performance → screen-ready shapes ──────────────
  const round1 = (n) => Math.round(n * 10) / 10;
  const byDateAsc = (perf) => [...(perf.byDate || [])].sort((a, b) => (a.keys[0] < b.keys[0] ? -1 : 1));

  // Industry-average organic CTR by Google position — used to spot queries that
  // rank well but get fewer clicks than they should (a title/snippet problem).
  const CTR_CURVE = { 1: 0.28, 2: 0.15, 3: 0.11, 4: 0.08, 5: 0.06, 6: 0.05, 7: 0.04, 8: 0.032, 9: 0.028, 10: 0.025 };
  function expectedCtr(pos) {
    const p = Math.round(pos);
    if (p <= 10) return CTR_CURVE[Math.max(1, p)] || 0.025;
    if (p <= 20) return 0.015;
    return 0.006;
  }

  const transforms = {
    // headline numbers
    summary(perf) {
      const q = perf.byQuery || [];
      return {
        tracked: q.length,
        top3: q.filter((r) => r.position <= 3).length,
        top10: q.filter((r) => r.position <= 10).length,
        avgPos: round1(perf.totals.position),
        clicks: perf.totals.clicks,
        impressions: perf.totals.impressions,
        ctr: perf.totals.ctr,
      };
    },
    // ranking table rows (real queries, best→ by clicks then impressions)
    rows(perf) {
      return (perf.byQuery || []).map((r) => ({
        kw: r.keys[0],
        pos: round1(r.position),
        clicks: r.clicks,
        impressions: r.impressions,
        ctr: r.ctr,
      }));
    },
    // daily series for sparklines / trend lines
    clicksSeries: (perf) => byDateAsc(perf).map((r) => r.clicks),
    imprSeries: (perf) => byDateAsc(perf).map((r) => r.impressions),
    posSeries: (perf) => byDateAsc(perf).map((r) => r.position),
    // ── The CHOSEN keyword set ────────────────────────────────────
    // The client picks keywords (their own phrases + our suggestions); those —
    // and only those — are tracked. These three helpers are the SINGLE source
    // used by both the Keywords screen and Rank Tracking, so their numbers can
    // never disagree. Position comes from our real rank check, falling back to
    // Search Console; clicks/CTR always come from Search Console.
    trackedRows(tracked, perf) {
      if (!tracked) return null;
      const g = new Map();
      if (perf) for (const r of (perf.byQuery || [])) g.set(((r.keys && r.keys[0]) || '').toLowerCase(), r);
      return tracked.map((k) => {
        const m = g.get((k.keyword || '').toLowerCase());
        const pos = k.position != null ? k.position : (m ? round1(m.position) : null);
        const change = (k.position != null && k.previous_position != null) ? (k.previous_position - k.position) : null; // + = moved up
        return {
          kw: k.keyword, pos, change,
          volume: k.volume != null ? k.volume : null,
          difficulty: k.difficulty != null ? k.difficulty : null,
          clicks: m ? m.clicks : null, impressions: m ? m.impressions : null, ctr: m ? m.ctr : null,
          // When we last looked. Without this, "checked and not in the top 100"
          // looks identical to "never checked".
          checked: k.checked_at || null,
        };
      }).sort((a, b) => (a.pos == null ? 1 : b.pos == null ? -1 : a.pos - b.pos));
    },
    trackedSummary(rows) {
      const r = rows || [];
      const ranked = r.filter((x) => x.pos != null);
      return {
        total: r.length,
        ranked: ranked.length,
        top3: ranked.filter((x) => x.pos <= 3).length,
        top10: ranked.filter((x) => x.pos <= 10).length,
        avgPos: ranked.length ? round1(ranked.reduce((a, x) => a + x.pos, 0) / ranked.length) : null,
        improved: r.filter((x) => x.change != null && x.change > 0).length,
        clicks: r.reduce((a, x) => a + (x.clicks || 0), 0),
      };
    },
    trackedDistribution(rows) {
      const q = (rows || []).filter((x) => x.pos != null);
      const b = (lo, hi) => q.filter((x) => x.pos >= lo && x.pos <= hi).length;
      return [
        { label: '1–3', n: b(0, 3) },
        { label: '4–10', n: b(3.0001, 10) },
        { label: '11–20', n: b(10.0001, 20) },
        { label: '21–50', n: b(20.0001, 50) },
        { label: '50+', n: q.filter((x) => x.pos > 50).length },
      ];
    },
    // position distribution buckets
    distribution(perf) {
      const q = perf.byQuery || [];
      const b = (lo, hi) => q.filter((r) => r.position >= lo && r.position <= hi).length;
      return [
        { label: '1–3', n: b(0, 3) },
        { label: '4–10', n: b(3.0001, 10) },
        { label: '11–20', n: b(10.0001, 20) },
        { label: '21–50', n: b(20.0001, 50) },
        { label: '50+', n: q.filter((r) => r.position > 50).length },
      ];
    },
    // Keyword analysis & scoring — all derived from real Search Console data.
    //  • a 0–100 keyword-health score (position reach + CTR vs expected)
    //  • per-query opportunity score + an action bucket (quick win, fix CTR…)
    keywordAnalysis(perf) {
      const q = (perf.byQuery || []).filter((r) => r.impressions > 0);
      if (!q.length) return { score: 0, opportunities: [], buckets: {}, avgPos: 0 };
      const maxImp = Math.max(...q.map((r) => r.impressions));
      const logMax = Math.log10(1 + maxImp) || 1;

      const opportunities = q.map((r) => {
        const exp = expectedCtr(r.position);
        const impFactor = Math.min(1, Math.log10(1 + r.impressions) / logMax); // demand weight
        const ctrGap = Math.max(0, exp - r.ctr);
        let bucket, action, opp;
        if (r.position >= 8 && r.position <= 20) {
          bucket = 'Quick win'; action = 'On page 2 with real demand — a push lands it on page 1.';
          opp = 100 * impFactor * (0.55 + 0.45 * ((20 - r.position) / 12));
        } else if (r.position < 8 && r.ctr < exp) {
          bucket = 'Fix CTR'; action = 'Ranks well but under-clicked — rewrite the title & meta.';
          opp = 100 * impFactor * Math.min(1, ctrGap / (exp || 1));
        } else if (r.position <= 3) {
          bucket = 'Top performer'; action = 'Already winning — protect this ranking.';
          opp = 12 * impFactor;
        } else {
          bucket = 'Long tail'; action = 'Low volume — group into a content theme.';
          opp = 22 * impFactor;
        }
        return {
          kw: r.keys[0], position: round1(r.position), impressions: r.impressions,
          clicks: r.clicks, ctr: r.ctr, expectedCtr: exp,
          oppScore: Math.max(1, Math.round(opp)), bucket, action,
        };
      }).sort((a, b) => b.oppScore - a.oppScore);

      const top10 = q.filter((r) => r.position <= 10).length;
      const totalClicks = q.reduce((a, r) => a + r.clicks, 0);
      const expClicks = q.reduce((a, r) => a + expectedCtr(r.position) * r.impressions, 0);
      const avgPos = q.reduce((a, r) => a + r.position, 0) / q.length;
      const posScore = Math.max(0, Math.min(100, 100 - (avgPos - 1) * 3));      // pos1→100, ~34→0
      const reachScore = Math.min(100, (top10 / q.length) * 100);                // share in top 10
      const ctrScore = Math.min(100, expClicks ? (totalClicks / expClicks) * 70 : 0); // vs expected clicks
      const score = Math.round(0.45 * posScore + 0.3 * reachScore + 0.25 * ctrScore);

      const buckets = {};
      opportunities.forEach((r) => { buckets[r.bucket] = (buckets[r.bucket] || 0) + 1; });
      return { score, opportunities, buckets, avgPos: round1(avgPos) };
    },
  };

  window.SGLive = {
    useLive, useAccount, useGeo, fetchSites, fetchPerf, setCurrentSite, prettySite, transforms,
    fetchAccount, addDomain, removeDomain, fetchClientMe, fetchGeoSummary, fetchGeoMentions,
    runGeoScan, runSeoAudit, fetchLatestAudit, researchKeywords, fetchBacklinks, fetchCompetitors,
    fetchReferringDomains, generateReport, sendReport, scheduleReport, fetchScheduledReports, toggleScheduledReport, deleteScheduledReport, fetchReportOverview, useEntitlement, PaidLock, useBranding, fetchKeywordSuggestions,
    fetchApprovedKeywords, approveKeywords, checkRanks, removeApprovedKeyword, generateContent, fetchWebsiteKeywords, fetchSuggestedKeywords, fetchSimilarKeywords, fetchKeywordIdeas, fetchInternalLinks, fetchTrackedKeywords, fetchKeywordGap, fetchAutomations, setAutomation, fetchProfile, saveProfile, useUsageVersion, bumpUsage, fetchDetectedProfile, detectProfile, moreVideoTags, fetchPlans,
    login, logout, startAuth, fetchWhoami, useAuthGate, changeOwnPassword,
    fetchSavedCompetitors, addCompetitor, removeCompetitor, compareCompetitors, fetchBacklinkOpportunities, checkGoogleListing,
    fetchAgentReadiness, startReviewPull, collectReviews,
    fetchYouTubeVideos, optimizeYouTubeVideo, saveYouTubeVideo, connectGoogle, reconnectGoogle, useReauth,
    adminListClients, adminUpdateClient, adminIssueLink, adminSetPassword, adminAuditLog, adminCreateClient, adminDeleteClient, adminInvite,
    adminListPending, adminEnroll, adminDismissPending, apiFetch, getKey, API,
  };
})();
